import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Divider,
	FormControlLabel,
	Grid,
	MenuItem,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import {
	BILL_LOGO,
	BILL_PREVIEW_HTML,
	BILL_SETTINGS,
	REMOVE_BILL_LOGO,
	SAVE_BILL_SETTINGS,
	UPLOAD_BILL_LOGO,
} from '../../gql/queries';
import { useShopDetails } from '../../hooks/use-shop';
import { useAuthContext } from '../../contexts/auth-context';
import { currencyLabel, formatMoney, rememberCurrency, SUPPORTED_CURRENCIES } from '../../utils/money';
import { LoadingButton } from '../generic/LoadingButton';

type ShowKey = 'address' | 'phone' | 'email' | 'gstin' | 'customerPhone' | 'paymentMethod' | 'staff' | 'discountColumn';

interface Draft {
	currency: string;
	printedName: string;
	gstin: string;
	headerNote: string;
	footerText: string;
	accentColor: string;
	receiptPaper: string;
	show: Record<ShowKey, boolean>;
}

const SHOW_LABELS: Array<[ShowKey, string]> = [
	['address', 'Branch address'],
	['phone', 'Branch phone'],
	['email', 'Branch email'],
	['gstin', 'GSTIN'],
	['customerPhone', "Customer's phone"],
	['paymentMethod', 'Payment method'],
	['staff', 'Staff name on each line'],
	['discountColumn', 'Discount column'],
];

const PAPER_LABELS: Record<string, string> = {
	THERMAL_58: 'Thermal 58 mm',
	THERMAL_80: 'Thermal 80 mm',
	A4: 'A4',
};

const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const LOGO_MAX_BYTES = 300 * 1024;

// The API fills an unset printed name with the shop name. Shown as empty, so a
// save does not freeze today's shop name onto the bills after a rename.
const toDraft = (s: any, shopName: string): Draft => ({
	currency: s.currency,
	printedName: s.printedName && s.printedName !== shopName ? s.printedName : '',
	gstin: s.gstin ?? '',
	headerNote: s.headerNote ?? '',
	footerText: s.footerText ?? '',
	accentColor: s.accentColor,
	receiptPaper: s.receiptPaper,
	show: { ...s.show },
});

/** What the API's merge expects: every field named, empty text meaning "back to the default". */
const toInput = (d: Draft) => ({
	currency: d.currency,
	printedName: d.printedName.trim(),
	gstin: d.gstin.replace(/\s+/g, '').toUpperCase(),
	headerNote: d.headerNote.trim(),
	footerText: d.footerText.trim(),
	accentColor: d.accentColor,
	receiptPaper: d.receiptPaper,
	show: d.show,
});

/**
 * Settings → Bill & currency (openspec/changes/location-bill-settings).
 *
 * How bills look for one location — the WhatsApp PDF and the till receipt —
 * and the currency it shows money in. The preview on the right is rendered by
 * the API from the unsaved draft, so it is exactly what the PDF will print.
 */
export const BillSettings = () => {
	const { shopId, locationId: activeLocationId } = useShopDetails();
	const { shopDetails } = useAuthContext();
	const client = useApolloClient();
	const locations = useMemo(() => ((shopDetails?.location as any[]) ?? []).filter((l) => l?._id), [shopDetails]);
	const shopName = shopDetails?.name ?? '';
	const [locationId, setLocationId] = useState<string>(activeLocationId);

	const { data, loading, error, refetch } = useQuery(BILL_SETTINGS, {
		variables: { shopId, locationId },
		skip: !shopId || !locationId,
		fetchPolicy: 'network-only',
	});
	const { data: logoData, refetch: refetchLogo } = useQuery(BILL_LOGO, {
		variables: { shopId, locationId },
		skip: !shopId || !locationId,
		fetchPolicy: 'network-only',
	});
	const [saveSettings, { loading: saving }] = useMutation(SAVE_BILL_SETTINGS);
	const [uploadLogo, { loading: uploading }] = useMutation(UPLOAD_BILL_LOGO);
	const [removeLogo, { loading: removing }] = useMutation(REMOVE_BILL_LOGO);

	const [draft, setDraft] = useState<Draft | null>(null);
	const [applyGstinToAll, setApplyGstinToAll] = useState(false);
	const [previewHtml, setPreviewHtml] = useState<string>('');
	const [previewError, setPreviewError] = useState<string | null>(null);
	const fileInput = useRef<HTMLInputElement>(null);

	// The draft is taken from the saved settings once per branch. The settings are
	// refetched after a logo upload or removal too (hasLogo, logoUpdatedAt), and
	// that must not throw away what the admin has typed but not saved yet.
	const draftFor = useRef<string | null>(null);
	useEffect(() => {
		if (!data?.billSettings) return;
		if (draft && draftFor.current === locationId) return;
		setDraft(toDraft(data.billSettings, shopName));
		draftFor.current = locationId;
		// eslint-disable-next-line
	}, [data]);

	const saved = data?.billSettings ? toDraft(data.billSettings, shopName) : null;
	const dirty = !!draft && !!saved && JSON.stringify(toInput(draft)) !== JSON.stringify(toInput(saved));
	const gstinValue = draft?.gstin.replace(/\s+/g, '').toUpperCase() ?? '';
	const gstinError = gstinValue !== '' && !GSTIN_PATTERN.test(gstinValue);
	const colorError = !!draft && !HEX_COLOR.test(draft.accentColor);
	const tooLong = !!draft && (draft.printedName.length > 80 || draft.headerNote.length > 200 || draft.footerText.length > 500);
	const invalid = gstinError || colorError || tooLong;
	// Ticking "all branches" is a change of its own: it copies a GSTIN that is already saved here.
	const canSave = (dirty || (applyGstinToAll && locations.length > 1)) && !invalid;

	// The preview follows the draft, debounced; invalid drafts keep the last good preview.
	useEffect(() => {
		if (!draft || invalid) return;
		const timer = window.setTimeout(async () => {
			try {
				const result = await client.query({
					query: BILL_PREVIEW_HTML,
					variables: { shopId, locationId, input: toInput(draft) },
					fetchPolicy: 'network-only',
				});
				setPreviewHtml(result.data?.billPreviewHtml ?? '');
				setPreviewError(null);
			} catch (error: any) {
				setPreviewError(error?.message ?? 'Preview unavailable');
			}
		}, 500);
		return () => window.clearTimeout(timer);
	}, [client, draft, invalid, shopId, locationId, data?.billSettings?.logoUpdatedAt]);

	const update = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => (d ? { ...d, [key]: value } : d));
	const updateShow = (key: ShowKey, value: boolean) => setDraft((d) => (d ? { ...d, show: { ...d.show, [key]: value } } : d));

	const handleSave = async () => {
		if (!draft || invalid) return;
		try {
			const result = await saveSettings({
				variables: { shopId, locationId, input: toInput(draft), applyGstinToAllLocations: applyGstinToAll },
			});
			const settings = result.data?.saveBillSettings;
			if (settings) setDraft(toDraft(settings, shopName));
			if (locationId === activeLocationId && settings) rememberCurrency(settings.currency);
			setApplyGstinToAll(false);
			await refetch();
			toast.success(applyGstinToAll ? 'Bill settings saved; GSTIN applied to every branch' : 'Bill settings saved');
		} catch (error: any) {
			toast.error(error?.message ?? 'Could not save the bill settings');
		}
	};

	const handleLogoFile = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) return;
		if (!LOGO_TYPES.includes(file.type)) {
			toast.error('The logo must be a PNG, JPEG or WebP image.');
			return;
		}
		if (file.size > LOGO_MAX_BYTES) {
			toast.error(`The logo is ${Math.ceil(file.size / 1024)} KB; the limit is 300 KB.`);
			return;
		}
		const dataUrl: string = await new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(String(reader.result));
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
		try {
			await uploadLogo({ variables: { shopId, locationId, dataUrl } });
			await Promise.all([refetch(), refetchLogo()]);
			toast.success('Logo uploaded');
		} catch (error: any) {
			toast.error(error?.message ?? 'Could not upload the logo');
		}
	};

	const handleRemoveLogo = async () => {
		if (!window.confirm('Remove the logo from this branch’s bills?')) return;
		try {
			await removeLogo({ variables: { shopId, locationId } });
			await Promise.all([refetch(), refetchLogo()]);
			toast.success('Logo removed');
		} catch (error: any) {
			toast.error(error?.message ?? 'Could not remove the logo');
		}
	};

	const locationName = useCallback(
		(id: string) => locations.find((l) => l._id === id)?.name || 'this branch',
		[locations]
	);

	if (loading && !draft) {
		return <Typography color="text.secondary">Loading bill settings…</Typography>;
	}
	if (!draft && error) {
		return <Alert severity="error">Could not load the bill settings: {error.message}</Alert>;
	}
	if (!draft) {
		return <Alert severity="info">Choose a shop and branch to set up its bills.</Alert>;
	}

	const logoUrl: string | null = logoData?.billLogo ?? null;

	return (
		<Grid container spacing={3}>
			<Grid item xs={12} lg={6}>
				<Stack spacing={3}>
					<Card>
						<CardContent>
							<Stack spacing={2.5}>
								<Box>
									<Typography variant="h6">Bill & currency</Typography>
									<Typography variant="body2" color="text.secondary">
										How bills look for {locationName(locationId)} — the WhatsApp PDF and the till receipt — and
										the currency money is shown in.
									</Typography>
								</Box>
								{locations.length > 1 && (
									<TextField
										select
										label="Branch"
										value={locationId}
										onChange={(e) => {
											// Another branch has its own settings; the draft for this one would be lost.
											if (dirty && !window.confirm('Discard the unsaved changes to this branch?')) return;
											setLocationId(e.target.value);
										}}
										fullWidth
									>
										{locations.map((l) => (
											<MenuItem key={l._id} value={l._id}>
												{l.name}
												{l._id === activeLocationId ? ' (current)' : ''}
											</MenuItem>
										))}
									</TextField>
								)}
								<TextField
									label="Name printed on the bill"
									value={draft.printedName}
									onChange={(e) => update('printedName', e.target.value)}
									helperText={draft.printedName.length > 80 ? 'At most 80 characters' : shopName ? `Leave empty to print the shop name, ${shopName}` : 'Leave empty to use the shop name'}
									error={draft.printedName.length > 80}
									fullWidth
								/>
								<TextField
									select
									label="Currency"
									value={draft.currency}
									onChange={(e) => update('currency', e.target.value)}
									helperText={`Amounts are shown as ${formatMoney(1250, draft.currency)}. They are labelled, never converted.`}
									fullWidth
								>
									{SUPPORTED_CURRENCIES.map((code) => (
										<MenuItem key={code} value={code}>
											{currencyLabel(code)}
										</MenuItem>
									))}
								</TextField>
								<Box>
									<TextField
										label="GSTIN"
										value={draft.gstin}
										onChange={(e) => update('gstin', e.target.value.toUpperCase())}
										error={gstinError}
										helperText={gstinError ? '15 characters, e.g. 29ABCDE1234F1Z5' : 'This branch’s GST registration. Leave empty if it has none.'}
										inputProps={{ maxLength: 15, style: { fontFamily: 'monospace', letterSpacing: 1 } }}
										fullWidth
									/>
									{locations.length > 1 && (
										<FormControlLabel
											control={<Checkbox checked={applyGstinToAll} onChange={(e) => setApplyGstinToAll(e.target.checked)} />}
											label={`Use this GSTIN for all ${locations.length} branches of the shop`}
										/>
									)}
								</Box>
							</Stack>
						</CardContent>
					</Card>

					<Card>
						<CardContent>
							<Stack spacing={2}>
								<Typography variant="subtitle1" fontWeight={600}>Logo</Typography>
								<Stack direction="row" spacing={2} alignItems="center">
									<Box
										sx={{
											width: 160,
											height: 64,
											border: 1,
											borderColor: 'divider',
											borderRadius: 1,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											bgcolor: 'background.default',
											overflow: 'hidden',
										}}
									>
										{logoUrl ? (
											<img src={logoUrl} alt="Bill logo" style={{ maxWidth: '100%', maxHeight: '100%' }} />
										) : (
											<Typography variant="caption" color="text.secondary">No logo</Typography>
										)}
									</Box>
									<Stack direction="row" spacing={1}>
										<input ref={fileInput} type="file" accept={LOGO_TYPES.join(',')} hidden onChange={handleLogoFile} />
										<LoadingButton variant="outlined" loading={uploading} onClick={() => fileInput.current?.click()}>
											{logoUrl ? 'Replace' : 'Upload'}
										</LoadingButton>
										{logoUrl && (
											<LoadingButton color="error" loading={removing} onClick={handleRemoveLogo}>
												Remove
											</LoadingButton>
										)}
									</Stack>
								</Stack>
								<Typography variant="caption" color="text.secondary">
									PNG, JPEG or WebP, up to 300 KB. A wide logo on a white or transparent background prints best.
								</Typography>
							</Stack>
						</CardContent>
					</Card>

					<Card>
						<CardContent>
							<Stack spacing={2.5}>
								<Typography variant="subtitle1" fontWeight={600}>Header & footer</Typography>
								<TextField
									label="Note under the address"
									placeholder="e.g. Open 10 am – 9 pm, all days"
									value={draft.headerNote}
									onChange={(e) => update('headerNote', e.target.value)}
									error={draft.headerNote.length > 200}
									helperText={`${draft.headerNote.length}/200`}
									multiline
									minRows={2}
									fullWidth
								/>
								<TextField
									label="Footer"
									value={draft.footerText}
									onChange={(e) => update('footerText', e.target.value)}
									error={draft.footerText.length > 500}
									helperText={`${draft.footerText.length}/500 · leave empty for “Thank you for your business!”`}
									multiline
									minRows={2}
									fullWidth
								/>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
									<TextField
										label="Accent colour"
										value={draft.accentColor}
										onChange={(e) => update('accentColor', e.target.value)}
										error={colorError}
										helperText={colorError ? 'Like #1A2B3C' : 'Name, lines and borders'}
										InputProps={{
											startAdornment: (
												<input
													type="color"
													aria-label="Pick the accent colour"
													value={HEX_COLOR.test(draft.accentColor) ? draft.accentColor : '#333333'}
													onChange={(e) => update('accentColor', e.target.value.toUpperCase())}
													style={{ width: 32, height: 32, border: 'none', background: 'none', marginRight: 8, padding: 0 }}
												/>
											),
										}}
										fullWidth
									/>
									<TextField
										select
										label="Till receipt paper"
										value={draft.receiptPaper}
										onChange={(e) => update('receiptPaper', e.target.value)}
										helperText="The WhatsApp PDF is always A4"
										fullWidth
									>
										{Object.entries(PAPER_LABELS).map(([value, label]) => (
											<MenuItem key={value} value={value}>{label}</MenuItem>
										))}
									</TextField>
								</Stack>
							</Stack>
						</CardContent>
					</Card>

					<Card>
						<CardContent>
							<Typography variant="subtitle1" fontWeight={600}>Show on the bill</Typography>
							<Grid container>
								{SHOW_LABELS.map(([key, label]) => (
									<Grid item xs={12} sm={6} key={key}>
										<FormControlLabel
											control={<Switch checked={draft.show[key]} onChange={(e) => updateShow(key, e.target.checked)} />}
											label={label}
										/>
									</Grid>
								))}
							</Grid>
						</CardContent>
					</Card>

					<Stack direction="row" spacing={2} justifyContent="flex-end">
						<Button disabled={!dirty || saving} onClick={() => saved && setDraft(saved)}>
							Discard changes
						</Button>
						<LoadingButton variant="contained" loading={saving} disabled={!canSave} onClick={handleSave}>
							Save
						</LoadingButton>
					</Stack>
				</Stack>
			</Grid>

			<Grid item xs={12} lg={6}>
				<Card sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
					<CardContent>
						<Stack direction="row" justifyContent="space-between" alignItems="baseline">
							<Typography variant="subtitle1" fontWeight={600}>Preview</Typography>
							<Typography variant="caption" color="text.secondary">
								{dirty ? 'Unsaved changes' : 'As customers receive it'}
							</Typography>
						</Stack>
						<Divider sx={{ my: 1.5 }} />
						{previewError && <Alert severity="warning" sx={{ mb: 1.5 }}>{previewError}</Alert>}
						<Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
							{/* Rendered by the API from escaped fields; sandboxed with no permissions at all. */}
							<iframe title="Bill preview" sandbox="" srcDoc={previewHtml} style={{ width: '100%', height: 720, border: 0 }} />
						</Box>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
};
